import React, { memo, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { useLockBodyScroll } from '@/hooks'
import { MarketShopOrderStatus, NullableType } from '@/models'
import { VERTICAL_OFFSET_PX } from '@/pages/market-shop-restaurant/orders-column/order-card-overlay/order-card-overlay.constants'

import { useMarketShopRestaurantStore } from '../../market-shop-restaurant.store'
import { OrderCard } from '../order-card/order-card'
import * as S from './order-card-overlay.styled'

export const OrderCardOverlay = memo(() => {
  const draggedOrder = useMarketShopRestaurantStore((store) => store.draggedOrder)
  const startDragPosition = useMarketShopRestaurantStore((store) => store.startDragPosition)
  const setDropTargetColumn = useMarketShopRestaurantStore((store) => store.setDropTargetColumn)

  const overlayContainerRef = useRef<HTMLDivElement>(null)

  useLockBodyScroll(!!draggedOrder)

  const handleSetOverlayStyles = (x: number, y: number) => {
    const overlay = overlayContainerRef.current
    if (!overlay) return
    overlay.style.transform = `translate(calc(-50% + ${x}px), calc(${y - VERTICAL_OFFSET_PX}px))`
  }

  useLayoutEffect(() => {
    const overlay = overlayContainerRef.current
    if (!overlay || !draggedOrder) return

    // Find the original card to get its width to set the overlay width to match it
    const originalCard = document.querySelector(`[data-order-number="${draggedOrder.id}"]`)
    if (originalCard) {
      overlay.style.width = `${originalCard.getBoundingClientRect().width}px`
    }
  }, [draggedOrder])

  useLayoutEffect(() => {
    handleSetOverlayStyles(startDragPosition.x, startDragPosition.y)
  }, [startDragPosition])

  useLayoutEffect(() => {
    if (!draggedOrder) return

    const handleMove = (clientX: number, clientY: number) => {
      handleSetOverlayStyles(clientX, clientY)

      // NOTE: Hack with 'pointerEvents' allows the browser to "see through" the overlay
      // and identify the underlying column, while preserving manipulating with cursor through styles
      const overlay = overlayContainerRef.current
      if (overlay) {
        overlay.style.pointerEvents = 'none'
      }

      // Identify the drop target
      const element = document.elementFromPoint(clientX, clientY)
      const column = element?.closest('[data-column-type]')
      setDropTargetColumn(
        column?.getAttribute('data-column-type') as NullableType<MarketShopOrderStatus>,
      )

      if (overlay) {
        overlay.style.pointerEvents = 'auto'
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault()
      }
      handleMove(e.touches[0].clientX, e.touches[0].clientY)
    }

    const handleMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY)
    }

    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('mousemove', handleMouseMove)

    return () => {
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [draggedOrder])

  if (!draggedOrder) return null

  return createPortal(
    <S.OverlayContainer ref={overlayContainerRef}>
      <OrderCard order={draggedOrder} isOverlay={true} />
    </S.OverlayContainer>,
    document.body,
  )
})
